/**
 * Travel arche settings page (tabbed).
 *
 * Tabs:
 *   - General: legacy localStorage preferences (time format, zoom, ...).
 *   - Profile: server-persisted traveler profile (diet, pace, budget, ...).
 *   - Features: per-feature on/off toggles for the whole arche.
 *
 * Each new feature added to the arche should register a key in
 * TRAVEL_DEFAULT_FEATURES (backend) and a labelled row in this Features tab.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Mail } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  getTravelSettings,
  setTravelSetting,
  togglePinnedTool,
  type TravelSettings,
  type PinnableToolId,
} from '../components/TravelSettings';
import { useTravelUserProfile, useTravelFeature, useAllTravelShareLinks } from '../hooks';
import { travelApi } from '../api';
import type { TravelerProfileFields } from '../api';
import { queryKeys } from '@/lib/query-keys';

const MOBILITY_OPTIONS: Array<{
  value: NonNullable<TravelerProfileFields['mobility']>;
  label: string;
}> = [
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'high', label: 'High' },
];

const PACE_OPTIONS: Array<{ value: NonNullable<TravelerProfileFields['pace']>; label: string }> = [
  { value: 'slow', label: 'Slow' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'packed', label: 'Packed' },
];

const BUDGET_OPTIONS: Array<{
  value: NonNullable<TravelerProfileFields['budget']>;
  label: string;
}> = [
  { value: 'shoestring', label: 'Shoestring' },
  { value: 'mid', label: 'Mid' },
  { value: 'premium', label: 'Premium' },
  { value: 'luxury', label: 'Luxury' },
];

/**
 * A flat list of feature keys rendered in the Features tab. Each feature must
 * add itself here when introduced so users can toggle it off.
 */
const FEATURE_ROWS: Array<{ featureKey: string; label: string; description: string }> = [
  {
    featureKey: 'traveler_profile',
    label: 'Traveler profile',
    description:
      'Store your diet, pace, budget, and other preferences so the AI can tailor its suggestions.',
  },
  {
    featureKey: 'map',
    label: 'Map view',
    description:
      'Show a map view with a pin per day and per event. Disabling this hides the Map button and stops address lookups.',
  },
  {
    featureKey: 'dossier',
    label: 'Destination dossier',
    description:
      'AI-generated travel brief (visa, weather, currency, safety…) with live web citations. Disabling hides the Dossier button and blocks the AI from generating one.',
  },
  {
    featureKey: 'email_inbox',
    label: 'Email forwarding',
    description:
      'Forward booking confirmations to a personal address and the AI auto-populates your trip. Disabling stops parsing; the inbox still records what arrived so you can turn it back on without losing messages.',
  },
  {
    featureKey: 'flight_status',
    label: 'Flight status tracking',
    description:
      "Live status (scheduled / delayed / cancelled / landed) + gate + times for flight transits. Polls a third-party provider on an adaptive cadence. Disabling stops polling for your trips and hides the badge + the AI's get_flight_status tool.",
  },
  {
    featureKey: 'budget',
    label: 'Budget & expenses',
    description:
      "Per-trip budget with multi-currency expenses, per-category + per-day breakdowns, and split accounting. Disabling hides the Budget button, the quick-add controls, and the AI's expense tools. Existing expenses remain in the database.",
  },
  {
    featureKey: 'packing_list',
    label: 'Packing list',
    description:
      "Trip-scoped packing checklist with an AI generator that tailors suggestions to your cities, dates, and traveler profile. Disabling hides the Packing button and the AI's generate_packing_list tool.",
  },
  {
    featureKey: 'reservation_scout',
    label: 'Reservation scout',
    description:
      "Uses live web search to flag which food events need advance booking, with a per-event details sheet + draft email. Disabling hides the badges, the Reservations button, and the AI's scout_reservations tool.",
  },
  {
    featureKey: 'critic',
    label: 'Trip analysis (critic)',
    description:
      'Sectioned critique of pacing, logistics, operating hours, reservations, health/jetlag, and missing basics. Disabling hides the Trip Analysis button and strips derived signals from the AI state snapshot.',
  },
  {
    featureKey: 'share_links',
    label: 'Share links',
    description:
      'Read-only public URLs for trips. Disabling hides the Share button and blocks creating new links, but existing active links keep working until you revoke them — manage them from Settings → Sharing.',
  },
];

function CsvField({
  id,
  label,
  value,
  onBlur,
}: {
  id: string;
  label: string;
  value: string[] | undefined;
  onBlur: (next: string[]) => void;
}) {
  const [text, setText] = useState((value ?? []).join(', '));
  useEffect(() => {
    setText((value ?? []).join(', '));
  }, [value]);
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() =>
          onBlur(
            text
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          )
        }
        placeholder="Comma-separated"
      />
    </div>
  );
}

function GeneralTab() {
  const [settings, setSettings] = useState<TravelSettings>(getTravelSettings);

  const update = <K extends keyof TravelSettings>(key: K, value: TravelSettings[K]) => {
    setTravelSetting(key, value);
    setSettings({ ...settings, [key]: value });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Time Format</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <button
              className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                settings.timeFormat === '12h'
                  ? 'bg-foreground text-background border-foreground'
                  : 'border-border hover:bg-muted'
              }`}
              onClick={() => update('timeFormat', '12h')}
            >
              <div className="font-medium">12-hour</div>
              <div className="text-xs mt-0.5 opacity-70">2:30 PM</div>
            </button>
            <button
              className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                settings.timeFormat === '24h'
                  ? 'bg-foreground text-background border-foreground'
                  : 'border-border hover:bg-muted'
              }`}
              onClick={() => update('timeFormat', '24h')}
            >
              <div className="font-medium">24-hour</div>
              <div className="text-xs mt-0.5 opacity-70">14:30</div>
            </button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Minute Interval</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <button
              className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                settings.minuteInterval === 5
                  ? 'bg-foreground text-background border-foreground'
                  : 'border-border hover:bg-muted'
              }`}
              onClick={() => update('minuteInterval', 5)}
            >
              <div className="font-medium">5 minutes</div>
              <div className="text-xs mt-0.5 opacity-70">00, 05, 10, 15...</div>
            </button>
            <button
              className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                settings.minuteInterval === 1
                  ? 'bg-foreground text-background border-foreground'
                  : 'border-border hover:bg-muted'
              }`}
              onClick={() => update('minuteInterval', 1)}
            >
              <div className="font-medium">1 minute</div>
              <div className="text-xs mt-0.5 opacity-70">00, 01, 02, 03...</div>
            </button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Zoom Controls</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <button
              className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                settings.zoomEnabled
                  ? 'bg-foreground text-background border-foreground'
                  : 'border-border hover:bg-muted'
              }`}
              onClick={() => update('zoomEnabled', true)}
            >
              <div className="font-medium">Enabled</div>
              <div className="text-xs mt-0.5 opacity-70">Show +/- buttons</div>
            </button>
            <button
              className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                !settings.zoomEnabled
                  ? 'bg-foreground text-background border-foreground'
                  : 'border-border hover:bg-muted'
              }`}
              onClick={() => update('zoomEnabled', false)}
            >
              <div className="font-medium">Disabled</div>
              <div className="text-xs mt-0.5 opacity-70">Hide zoom controls</div>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ProfileTab() {
  const { profile, updateProfile, loading, isSaving } = useTravelUserProfile();
  const profileFeature = useTravelFeature('traveler_profile');

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading profile…</div>;
  }

  if (!profileFeature.enabled) {
    return (
      <Card>
        <CardContent className="py-6 space-y-2">
          <p className="text-sm">
            The traveler profile is currently disabled. Re-enable it from the{' '}
            <span className="font-medium">Features</span> tab to tell the AI about your preferences.
          </p>
        </CardContent>
      </Card>
    );
  }

  const save = (patch: Partial<TravelerProfileFields>) => {
    void updateProfile(patch);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <CsvField id="diet" label="Diet" value={profile.diet} onBlur={(diet) => save({ diet })} />
          <CsvField
            id="allergies"
            label="Allergies"
            value={profile.allergies}
            onBlur={(allergies) => save({ allergies })}
          />
          <CsvField
            id="interests"
            label="Interests"
            value={profile.interests}
            onBlur={(interests) => save({ interests })}
          />
          <CsvField
            id="dislikes"
            label="Dislikes"
            value={profile.dislikes}
            onBlur={(dislikes) => save({ dislikes })}
          />
          <CsvField
            id="languages"
            label="Languages"
            value={profile.languages}
            onBlur={(languages) => save({ languages })}
          />

          <div className="space-y-1">
            <Label>Mobility</Label>
            <div className="flex gap-2">
              {MOBILITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                    profile.mobility === opt.value
                      ? 'bg-foreground text-background border-foreground'
                      : 'border-border hover:bg-muted'
                  }`}
                  onClick={() => save({ mobility: opt.value })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label>Pace</Label>
            <div className="flex gap-2">
              {PACE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                    profile.pace === opt.value
                      ? 'bg-foreground text-background border-foreground'
                      : 'border-border hover:bg-muted'
                  }`}
                  onClick={() => save({ pace: opt.value })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label>Budget</Label>
            <div className="flex gap-2 flex-wrap">
              {BUDGET_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`flex-1 min-w-[120px] px-3 py-2 rounded-md border text-sm transition-colors ${
                    profile.budget === opt.value
                      ? 'bg-foreground text-background border-foreground'
                      : 'border-border hover:bg-muted'
                  }`}
                  onClick={() => save({ budget: opt.value })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="homeCity">Home city</Label>
            <Input
              id="homeCity"
              defaultValue={profile.homeCity ?? ''}
              onBlur={(e) => {
                const v = e.target.value;
                if (v !== (profile.homeCity ?? '')) save({ homeCity: v });
              }}
              placeholder="e.g., San Francisco"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              defaultValue={profile.notes ?? ''}
              onBlur={(e) => {
                const v = e.target.value;
                if (v !== (profile.notes ?? '')) save({ notes: v });
              }}
              placeholder="Anything else the AI should know"
              rows={3}
            />
          </div>

          {isSaving && <div className="text-xs text-muted-foreground">Saving…</div>}
        </CardContent>
      </Card>
    </div>
  );
}

function FeaturesTab() {
  return (
    <div className="space-y-4">
      {FEATURE_ROWS.map((row) => (
        <FeatureRow
          key={row.featureKey}
          featureKey={row.featureKey}
          label={row.label}
          description={row.description}
        />
      ))}
    </div>
  );
}

function DossierTab() {
  const feature = useTravelFeature('dossier');
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Destination dossier</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Generates a concise travel brief for your trip using an AI assistant with live web
            search. Covers visa/entry, weather outlook for your dates, currency, tipping, adapters,
            safety advisories, health/vaccines, key phrases, and customs — each with citations so
            you can verify the source.
          </p>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Enable destination dossier</div>
            <Switch
              checked={feature.enabled}
              onCheckedChange={(next) => void feature.toggle(next)}
              disabled={feature.isToggling}
              aria-label="Toggle destination dossier"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Disabling hides the Dossier button in the trip header and prevents the AI from
            regenerating a dossier on your behalf.
          </p>
          <PinToToolbarRow toolId="dossier" label="Dossier" />
        </CardContent>
      </Card>
    </div>
  );
}

function EmailForwardingTab() {
  const feature = useTravelFeature('email_inbox');
  const { updatedAt } = useTravelUserProfile();
  // Re-read the profile so we pick up `inboundAddress`; the main hook already
  // has it in cache but its return shape doesn't expose the field directly.
  const { data: raw } = useInboundAddressQuery();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const inboundAddress = raw?.inboundAddress ?? null;

  const handleCopy = () => {
    if (!inboundAddress) return;
    void navigator.clipboard.writeText(inboundAddress).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Email forwarding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Forward a booking confirmation to the address below and the AI extracts the flight,
            hotel, or reservation and adds it to your trip. You can see every forwarded email in the{' '}
            <span className="font-medium">Inbox</span>, re-parse anything that came back unassigned,
            or assign a message to a different trip.
          </p>

          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Enable email forwarding</div>
            <Switch
              checked={feature.enabled}
              onCheckedChange={(next) => void feature.toggle(next)}
              disabled={feature.isToggling}
              aria-label="Toggle email forwarding"
            />
          </div>

          {feature.enabled ? (
            inboundAddress ? (
              <div className="space-y-2">
                <Label className="text-xs">Your forwarding address</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={inboundAddress}
                    className="font-mono text-sm"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleCopy}
                    aria-label="Copy forwarding address"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5 mr-1" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Forward confirmations from airlines, hotels, and booking platforms. Duplicates are
                  automatically deduped by confirmation number.
                </p>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                Your forwarding address is being provisioned. It will appear here shortly — try
                refreshing this page.
              </div>
            )
          ) : (
            <div className="text-xs text-muted-foreground">
              While disabled, any email that arrives is logged as <em>Unassigned</em> and not
              parsed. Re-enable the feature to process the backlog.
            </div>
          )}

          <div className="pt-2 flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => navigate('/a/arche_travel/inbox')}>
              <Mail className="h-3.5 w-3.5 mr-1" /> Open inbox
            </Button>
          </div>

          {updatedAt && (
            <p className="text-xs text-muted-foreground pt-2 border-t">
              Profile updated {new Date(updatedAt).toLocaleString()}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Hits the profile endpoint to read the server-generated forwarding address.
 * Uses the same query key as the main profile hook so data is shared.
 */
function useInboundAddressQuery() {
  return useQuery({
    queryKey: queryKeys.travel.profile(),
    queryFn: () => travelApi.getTravelUserProfile(),
  });
}

const BUDGET_CURRENCY_OPTIONS = [
  'USD',
  'EUR',
  'GBP',
  'JPY',
  'CHF',
  'CAD',
  'AUD',
  'NZD',
  'SGD',
  'HKD',
  'CNY',
  'INR',
  'KRW',
  'MXN',
  'BRL',
  'ZAR',
  'THB',
  'VND',
];

function SharingTab() {
  const feature = useTravelFeature('share_links');
  const navigate = useNavigate();
  const { shareLinks, loading, revokeShareLink, isRevoking } = useAllTravelShareLinks();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Share links</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Create read-only public URLs for any of your trips. Links never expose your account —
            visitors see only the itinerary, with short-TTL signed URLs for attachments.
          </p>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Enable share links</div>
            <Switch
              checked={feature.enabled}
              onCheckedChange={(next) => void feature.toggle(next)}
              disabled={feature.isToggling}
              aria-label="Toggle share links"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Disabling hides the Share button on the trip page and rejects new link creation with
            403. <em>Existing active links continue to work</em> until you revoke them below.
          </p>

          <div className="border-t border-border pt-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
              Active links across all your trips
            </div>
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : shareLinks.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">No active links.</div>
            ) : (
              <ul className="space-y-2">
                {shareLinks.map((link) => {
                  const url =
                    typeof window !== 'undefined'
                      ? `${window.location.origin}/shared/travel/${link.shareId}`
                      : `/shared/travel/${link.shareId}`;
                  const expires = link.expiresAt
                    ? new Date(link.expiresAt).toLocaleString()
                    : 'No expiry';
                  return (
                    <li
                      key={link.shareId}
                      className="border border-border rounded-md p-2 space-y-1 bg-muted/20"
                    >
                      <div className="flex items-center justify-between text-xs">
                        <button
                          className="font-medium text-left hover:underline"
                          onClick={() => navigate(`/a/arche_travel/${link.tripId}`)}
                        >
                          {link.tripTitle || link.tripId}
                        </button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-[10px] text-destructive"
                          disabled={isRevoking}
                          onClick={() => void revokeShareLink(link.tripId, link.shareId)}
                        >
                          Revoke
                        </Button>
                      </div>
                      <Input
                        readOnly
                        value={url}
                        onFocus={(e) => e.currentTarget.select()}
                        className="font-mono text-[10px] h-7"
                      />
                      <div className="text-[10px] text-muted-foreground">{expires}</div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PackingTab() {
  const feature = useTravelFeature('packing_list');
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Packing list</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Opens a drawer on the trip view with a trip-scoped checklist. The &quot;Generate packing
            list&quot; button asks Claude to propose items tailored to your cities, dates, and
            traveler profile. Re-running is safe — duplicates are skipped.
          </p>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Enable packing list</div>
            <Switch
              checked={feature.enabled}
              onCheckedChange={(next) => void feature.toggle(next)}
              disabled={feature.isToggling}
              aria-label="Toggle packing list"
            />
          </div>
          <p className="text-xs text-muted-foreground pt-2 border-t border-border">
            Disabling hides the Packing button in the trip header and drops the AI&apos;s{' '}
            <code>generate_packing_list</code> tool. Stored todos are not deleted.
          </p>
          <PinToToolbarRow toolId="packing" label="Packing" />
        </CardContent>
      </Card>
    </div>
  );
}

function ReservationsTab() {
  const feature = useTravelFeature('reservation_scout');
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Reservation scout</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Classifies each food event on your trip as <em>no booking</em>, <em>suggested</em>,
            <em> required</em>, or <em>booked</em>, with the recommended booking method and a draft
            email where applicable. Backed by Claude with live web search for
            reservation-requirement confirmation.
          </p>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Enable reservation scout</div>
            <Switch
              checked={feature.enabled}
              onCheckedChange={(next) => void feature.toggle(next)}
              disabled={feature.isToggling}
              aria-label="Toggle reservation scout"
            />
          </div>
          <p className="text-xs text-muted-foreground pt-2 border-t border-border">
            Disabling hides the badges on food event cards, the Reservations button, and the
            AI&apos;s <code>scout_reservations</code> tool. Stored statuses remain in the database
            and reappear when re-enabled.
          </p>
          <PinToToolbarRow toolId="scout" label="Reservations" />
        </CardContent>
      </Card>
    </div>
  );
}

function CriticTab() {
  const feature = useTravelFeature('critic');
  const { profile, updateProfile, isSaving } = useTravelUserProfile();
  const [draft, setDraft] = useState<string>((profile.criticFocusAreas ?? []).join(', '));
  useEffect(() => {
    setDraft((profile.criticFocusAreas ?? []).join(', '));
  }, [profile.criticFocusAreas]);

  const saveFocusAreas = () => {
    const next = draft
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (JSON.stringify(next) !== JSON.stringify(profile.criticFocusAreas ?? [])) {
      void updateProfile({ criticFocusAreas: next });
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Trip analysis</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Runs a sectioned critique of your itinerary — pacing, logistics, operating hours,
            reservations, health &amp; jetlag, and missing basics. Each section is short and
            scannable; a &quot;Looks solid.&quot; tag appears where the critic has nothing to flag.
          </p>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Enable trip analysis</div>
            <Switch
              checked={feature.enabled}
              onCheckedChange={(next) => void feature.toggle(next)}
              disabled={feature.isToggling}
              aria-label="Toggle trip analysis"
            />
          </div>

          <div className="pt-2 border-t border-border space-y-2">
            <Label htmlFor="critic-extra" className="text-xs">
              Extra focus areas (optional, comma-separated)
            </Label>
            <Input
              id="critic-extra"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={saveFocusAreas}
              placeholder="solo safety, kid-friendly stops, vegan options"
            />
            <p className="text-[10px] text-muted-foreground">
              The critic folds these into the six standard sections rather than creating new
              headings, so the output stays scannable.
            </p>
            {isSaving && <div className="text-xs text-muted-foreground">Saving…</div>}
          </div>

          <p className="text-xs text-muted-foreground pt-2 border-t border-border">
            Disabling hides the Trip Analysis button and strips derived critic signals from the AI
            state snapshot.
          </p>
          <PinToToolbarRow toolId="critic" label="Trip Analysis" />
        </CardContent>
      </Card>
    </div>
  );
}

function BudgetTab() {
  const feature = useTravelFeature('budget');
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Budget &amp; expenses</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Track spending across currencies with automatic FX conversion. Each trip gets its own
            home currency + optional total cap (set from the Budget drawer on the trip view).
            Summaries show per-category, per-day, and per-person breakdowns.
          </p>

          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Enable budget tracking</div>
            <Switch
              checked={feature.enabled}
              onCheckedChange={(next) => void feature.toggle(next)}
              disabled={feature.isToggling}
              aria-label="Toggle budget tracking"
            />
          </div>

          <div className="text-xs text-muted-foreground space-y-1">
            <div>
              FX rates are provided by{' '}
              <a
                href="https://www.frankfurter.app/"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                Frankfurter
              </a>{' '}
              (keyless) and cached per-day. Expenses keep a snapshot of the rate so totals stay
              stable even if the provider updates older rates.
            </div>
            <div>
              Supported currencies (common): {BUDGET_CURRENCY_OPTIONS.slice(0, 8).join(', ')}… Any
              ISO 4217 three-letter code works in the quick-add dialog.
            </div>
          </div>

          <div className="text-xs text-muted-foreground pt-2 border-t border-border">
            Disabling hides the Budget button, the quick-add controls on days and events, and the
            AI's <code>add_expense</code> / <code>update_expense</code> /{' '}
            <code>delete_expense</code> tools. Stored expenses are not deleted — re-enable to see
            them again.
          </div>
          <PinToToolbarRow toolId="budget" label="Budget" />
        </CardContent>
      </Card>
    </div>
  );
}

function FlightStatusTab() {
  const feature = useTravelFeature('flight_status');
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Flight status tracking</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Shows live status (scheduled / delayed / cancelled / landed), gate numbers, and
            scheduled/actual times for every Flight transit on your trips. A background worker polls
            the provider on an adaptive cadence — hourly for flights more than 24 hours away, every
            5 minutes within 24 hours, every minute in the final 2 hours and while airborne.
          </p>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Enable flight status</div>
            <Switch
              checked={feature.enabled}
              onCheckedChange={(next) => void feature.toggle(next)}
              disabled={feature.isToggling}
              aria-label="Toggle flight status tracking"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Disabling halts polling for your account (the worker's SQL filter reads the same flag)
            and hides the badge in the transit editor. The AI's <code>get_flight_status</code> tool
            is also dropped from the arche's action set.
          </p>
          <div className="text-xs text-muted-foreground pt-2 border-t border-border space-y-1">
            <div className="font-medium">Notification delivery</div>
            <p>
              Status-change alerts currently surface as in-app toasts while you have this tab open.
              Browser push notifications and email delivery are not wired up yet — that is tracked
              as a follow-up once a user-scoped broadcast channel exists.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MapTab() {
  const feature = useTravelFeature('map');
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Map view</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Adds a Map button to the trip planner. Pins are placed for each day (from the day's
            city/country) and for events with coordinates. Disabling the feature hides the Map
            button, skips automatic address lookups on save, and prevents the AI from suggesting
            map-related actions.
          </p>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Enable map view</div>
            <Switch
              checked={feature.enabled}
              onCheckedChange={(next) => void feature.toggle(next)}
              disabled={feature.isToggling}
              aria-label="Toggle map view"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Geocoding uses OpenStreetMap's Nominatim service by default. Coordinates are only looked
            up when this feature is enabled.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function PinToToolbarRow({ toolId, label }: { toolId: PinnableToolId; label: string }) {
  const [pinned, setPinned] = useState(() => getTravelSettings().pinnedTools.includes(toolId));
  const handleToggle = () => {
    const next = togglePinnedTool(toolId);
    setPinned(next.includes(toolId));
  };
  return (
    <div className="flex items-center justify-between pt-2 border-t border-border">
      <div>
        <div className="text-sm font-medium">Pin to toolbar</div>
        <div className="text-xs text-muted-foreground">
          Show {label} as its own button in the trip header instead of inside the Tools menu.
        </div>
      </div>
      <Switch
        checked={pinned}
        onCheckedChange={handleToggle}
        aria-label={`Pin ${label} to toolbar`}
      />
    </div>
  );
}

function FeatureRow({
  featureKey,
  label,
  description,
}: {
  featureKey: string;
  label: string;
  description: string;
}) {
  const feature = useTravelFeature(featureKey);
  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-4 py-4">
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-muted-foreground">{description}</div>
        </div>
        <Switch
          checked={feature.enabled}
          onCheckedChange={(next) => void feature.toggle(next)}
          disabled={feature.isToggling}
          aria-label={`Toggle ${label}`}
        />
      </CardContent>
    </Card>
  );
}

export function TravelSettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Whoever opened Settings passes where to come back to (the trip the user was
  // in, the inbox, etc.) as a `returnTo` query param so it survives a refresh.
  // Only honor internal travel-arche paths — guards against an open-redirect via
  // a crafted link — and fall back to the trip list otherwise.
  const rawReturnTo = searchParams.get('returnTo');
  const returnTo =
    rawReturnTo && rawReturnTo.startsWith('/a/arche_travel') ? rawReturnTo : '/a/arche_travel';

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(returnTo)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold">Settings</h1>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-xl mx-auto">
          <Tabs defaultValue="general" className="w-full">
            {/* Tabs break into two rows since there are more than fit in a
                single row comfortably at narrow widths. */}
            <TabsList className="flex flex-wrap w-full text-xs h-auto">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="map">Map</TabsTrigger>
              <TabsTrigger value="dossier">Dossier</TabsTrigger>
              <TabsTrigger value="email">Email</TabsTrigger>
              <TabsTrigger value="flight">Flight</TabsTrigger>
              <TabsTrigger value="budget">Budget</TabsTrigger>
              <TabsTrigger value="packing">Packing</TabsTrigger>
              <TabsTrigger value="reservations">Reservations</TabsTrigger>
              <TabsTrigger value="critic">Trip Analysis</TabsTrigger>
              <TabsTrigger value="sharing">Sharing</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
            </TabsList>
            <TabsContent value="general" className="mt-4">
              <GeneralTab />
            </TabsContent>
            <TabsContent value="profile" className="mt-4">
              <ProfileTab />
            </TabsContent>
            <TabsContent value="map" className="mt-4">
              <MapTab />
            </TabsContent>
            <TabsContent value="dossier" className="mt-4">
              <DossierTab />
            </TabsContent>
            <TabsContent value="email" className="mt-4">
              <EmailForwardingTab />
            </TabsContent>
            <TabsContent value="flight" className="mt-4">
              <FlightStatusTab />
            </TabsContent>
            <TabsContent value="budget" className="mt-4">
              <BudgetTab />
            </TabsContent>
            <TabsContent value="packing" className="mt-4">
              <PackingTab />
            </TabsContent>
            <TabsContent value="reservations" className="mt-4">
              <ReservationsTab />
            </TabsContent>
            <TabsContent value="critic" className="mt-4">
              <CriticTab />
            </TabsContent>
            <TabsContent value="sharing" className="mt-4">
              <SharingTab />
            </TabsContent>
            <TabsContent value="features" className="mt-4">
              <FeaturesTab />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
